import { Component, Input, OnChanges, SimpleChanges } from "@angular/core";
import { DialogService, DynamicDialogRef } from "primeng/dynamicdialog";
import { MappingService } from "src/core/services/mapping.service";
import { MapDataComponent } from "../../modals/map-data/map-data.component";
import { EditMapDataComponent } from "../../modals/edit-data/edit-data.component";
import { ViewMapDataComponent } from "../../modals/view-data/view-data.component";
import { IssuerMappingService } from "src/api/services";
import { AsReportedIssuer, MappingFilter } from "src/api/models";
import { MessageService } from "primeng/api";
import {
  handlePasteWithoutLeadingSpaces,
  preventLeadingSpaceKey,
} from "src/core/utils/input-validation.utils";

@Component({
  selector: "app-issuer-processed",
  // imports: [],
  templateUrl: "./processed.component.html",
  styleUrl: "./processed.component.css",
  standalone: false,
})
export class IssuerProcessedComponent implements OnChanges {
  @Input() isActive: boolean = false;
  private hasLoadedOnce = false;
  ngOnChanges(changes: SimpleChanges): void {
    if (changes["isActive"]?.currentValue === true) {
      this.hasLoadedOnce = false;
    }
  }
  ref!: DynamicDialogRef;
  readonly paginator = { pageSize: 10, pageNumber: 1, totalCount: 0 };

  processedIssuers: AsReportedIssuer[] = [];
  selectedIssuers: any[] = [];
  selectedDialogRow: any = null;

  // Pagination for Issuers
  first = 0;
  rows = 10;
  issuerName?: string;
  cikNumber?: string;
  isLoading: boolean = false;
  constructor(
    private _issuerMappingService: IssuerMappingService,
    // public mappingService: MappingService,
    public dialogService: DialogService,
    private _messageService: MessageService
  ) {}

  ngOnInit(): void {}
  getProcessedIssuers() {
    const params: MappingFilter = {
      searchByName: this.issuerName,
      searchByCikNumber: this.cikNumber,
      pageNumber: this.paginator.pageNumber,
      pageSize: this.paginator.pageSize,
      status: "PROCESSED",
    };
    this.isLoading = true;
    this._issuerMappingService.getIssuerMapping(params).subscribe({
      next: (value) => {
        this.processedIssuers = value.processedIssuer ?? [];
      },
      error: (err) => {
        this.isLoading = false;
        this._messageService.add({
          summary: err.error.message,
          severity: "error",
        });
      },
      complete: () => {
        this.paginator.totalCount = this.processedIssuers?.[0]?.totalCount ?? 0;
        this.isLoading = false;
      },
    });
  }

  openEditMapModal(issuer?: any, event?: Event) {
    if (event) event.preventDefault();
    this.ref = this.dialogService.open(EditMapDataComponent, {
      dismissableMask: false,
      closable: true,
      modal: true,
      showHeader: true,
      style: { width: "60vw" },
      contentStyle: { overflow: "auto" },
      data: {
        issuerData: issuer,
        isEditMode: true,
      },
      header: "Edit Issuer Data",
    });

    this.ref.onClose.subscribe((refresh) => {
      if (refresh) {
        this.getProcessedIssuers();
      }
    });
  }
  openMapModal(issuer?: any, event?: Event) {
    if (event) event.preventDefault();
    this.ref = this.dialogService.open(MapDataComponent, {
      dismissableMask: false,
      closable: true,
      modal: true,
      showHeader: true,
      style: { width: "60vw" },
      contentStyle: { overflow: "auto" },
      header: "Mapping",
      data: {
        issuers: [issuer],
        remap: true,
      },
    });
    this.ref.onClose.subscribe((refresh) => {
      if (refresh) {
        this.getProcessedIssuers();
      }
    });
  }
  openViewMapModal(issuer: AsReportedIssuer, event?: Event) {
    if (event) event.preventDefault();

    if (!issuer?.issuerId) {
      this._messageService.add({
        severity: "warn",
        summary: "Warning",
        detail: "Issuer ID not found",
      });
      return;
    }

    this.isLoading = true;
    this._issuerMappingService
      .getIssuerMappingDetails(issuer.issuerId)
      .subscribe({
        next: (details: AsReportedIssuer[]) => {
          const detailData = details.length > 0 ? details : null;
          this.ref = this.dialogService.open(ViewMapDataComponent, {
            dismissableMask: false,
            closable: true,
            modal: true,
            showHeader: true,
            style: { width: "60vw" },
            contentStyle: { overflow: "auto" },
            header: "View Mapping Details",
            data: {
              issuerData: detailData,
              noRecord: !detailData,
            },
          });

          this.ref.onClose.subscribe((result) => {
            if (result?.revertIssuer) {
              this.openMapModal(result.revertIssuer);
            }
          });
        },
        error: (err) => {
          this.isLoading = false;
          this._messageService.add({
            severity: "error",
            summary: "Error",
            detail:
              err.error?.message || "Failed to load issuer mapping details",
          });
        },
        complete: () => {
          this.isLoading = false;
        },
      });
  }

  pageChange(event: any) {
    this.first = event.first;
    this.rows = event.rows;
  }

  onUpdateClick(issuer: any) {
    if (issuer) {
      this.openEditMapModal(issuer);
    }
  }

  onDetailClick(product: any) {
    if (product) {
      this.openViewMapModal(product);
    }
  }
  onDeleteClick(issuer: any) {
    if (!issuer?.issuerId) {
      this._messageService.add({
        severity: "warn",
        summary: "Warning",
        detail: "Issuer ID not found",
      });
      return;
    }

    const reason = "Deleted issuer successfully";
    this.isLoading = true;

    this._issuerMappingService
      .removeIssuer({ issuerId: issuer.issuerId, reason })
      .subscribe({
        next: (deleted: boolean) => {
          if (deleted) {
            this._messageService.add({
              severity: "success",
              summary: "Success",
              detail: "Issuer deleted successfully.",
            });
            this.getProcessedIssuers();
          } else {
            this._messageService.add({
              severity: "warn",
              summary: "Not Deleted",
              detail: "Issuer could not be deleted.",
            });
          }
        },
        error: (err) => {
          this.isLoading = false;
          this._messageService.add({
            severity: "error",
            summary: "Error",
            detail:
              err.error?.message ||
              "Failed to delete issuer. Please try again.",
          });
        },
        complete: () => {
          this.isLoading = false;
        },
      });
  }

  onSearch() {
    this.first = 0;
    this.paginator.pageNumber = 1;
    this.getProcessedIssuers();
  }

  loadData(event: any) {
    if (!this.isActive) return;
    if (!this.hasLoadedOnce && event.first === 0) {
      this.hasLoadedOnce = true;
    } else if (!this.hasLoadedOnce) {
      return;
    }

    if (event.first > 0 && this.paginator.pageSize !== event.rows) {
      this.paginator.pageNumber = 1;
      this.first = 0;
    } else {
      this.paginator.pageNumber = event.first / event.rows + 1;
      this.first = event.first;
    }

    this.paginator.pageSize = event.rows;
    this.getProcessedIssuers();
  }
  preventSpaceKey(event: KeyboardEvent): void {
    preventLeadingSpaceKey(event);
  }

  onPaste(event: ClipboardEvent, field: "issuerName" | "cikNumber"): void {
    handlePasteWithoutLeadingSpaces(event, (newValue: string) => {
      this[field] = newValue;
    });
  }
}
